import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import {OTPVerification, UnverifiedUserHandler} from 'components/Auth'
import { useTranslation } from 'react-i18next';


const Register = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [showUnverifiedHandler, setShowUnverifiedHandler] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  
  const { register, openLoginModal } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    console.log('handleSubmit called');
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('register.passwordTooShort'));
      setLoading(false);
      return;
    }

    if (formData.username.length < 3) {
      setError(t('register.usernameTooShort'));
      setLoading(false);
      return;
    }

    const registrationData = {
      username: formData.username,
      email: formData.email,
      password: formData.password,
      full_name: formData.full_name,
    };

    const result = await register(registrationData);
    
    console.log('Registration result:', result);
    
    if (result.success) {
      console.log('Registration successful, checking requires_verification:', result.requires_verification);
      if (result.requires_verification) {
        console.log('Showing OTP verification screen');
        // Show OTP verification screen
        setTempToken(result.temp_token);
        setRegisteredUser(result.user);
        setShowOTPVerification(true);
      } else {
        console.log('No verification required, navigating to home');
        // Legacy flow (should not happen)
        const returnPath = sessionStorage.getItem('registrationReturnPath');
        if (returnPath) {
          sessionStorage.removeItem('registrationReturnPath');
          navigate(returnPath);
        } else {
          navigate('/');
        }
      }
    } else {
      console.log('Registration failed:', result.error);
      
      // Check if the error is due to unverified email
      if (result.error && result.error.includes('Email already exists')) {
        // Show unverified user handler
        setUnverifiedEmail(formData.email);
        setShowUnverifiedHandler(true);
      } else {
        setError(result.error);
      }
    }
    
    setLoading(false);
  };

  const handleVerificationComplete = (verifiedUser, accessToken) => {
    // Registration and verification completed successfully
    const returnPath = sessionStorage.getItem('registrationReturnPath');
    if (returnPath) {
      sessionStorage.removeItem('registrationReturnPath');
      navigate(returnPath);
    } else {
      navigate('/');
    }
  };

  const handleBackToRegistration = () => {
    setShowOTPVerification(false);
    setShowUnverifiedHandler(false);
    setTempToken(null);
    setRegisteredUser(null);
    setUnverifiedEmail('');
    setError('');
  };

  // Show unverified user handler if needed
  if (showUnverifiedHandler) {
    return (
      <UnverifiedUserHandler
        email={unverifiedEmail}
        onVerificationComplete={handleVerificationComplete}
        onBack={handleBackToRegistration}
      />
    );
  }

  // Show OTP verification if needed
  if (showOTPVerification) {
    console.log('Rendering OTPVerification component');
    console.log('tempToken:', tempToken);
    console.log('registeredUser:', registeredUser);
    
    // Fallback if OTPVerification component is not available
    if (!OTPVerification) {
      console.error('OTPVerification component not found!');
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-blue-50 to-blue-100">
          <div className="max-w-md w-full space-y-8 p-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">OTP Verification</h2>
                <p className="text-gray-600">
                  OTP component not found. Please check the console for errors.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <OTPVerification
        tempToken={tempToken}
        user={registeredUser}
        onVerificationComplete={handleVerificationComplete}
      />
    );
  }

  console.log('Rendering registration form, showOTPVerification:', showOTPVerification);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-blue-50 to-blue-100">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center">
            <img 
              src="/logo3.png" 
              alt="ChemSafe" 
              className="mx-auto h-12 w-auto mb-4"
            />
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{t('register.title')}</h2>
            <p className="text-gray-600">{t('register.subtitle')}</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('register.username')}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('register.usernamePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('register.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('register.emailPlaceholder')}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {t('register.emailHint')}
                </p>
              </div>

              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('register.fullName')}
                </label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('register.fullNamePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('register.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('register.passwordPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('register.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={t('register.confirmPasswordPlaceholder')}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('register.creating')}
                  </div>
                ) : (
                  t('register.createButton')
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('register.haveAccount')}{' '}
                <button
                  onClick={() => {
                    // Get the saved return path if it exists
                    const returnPath = sessionStorage.getItem('registrationReturnPath');
                    
                    // Open login modal with the proper redirect path
                    if (returnPath) {
                      openLoginModal(returnPath);
                    } else {
                      openLoginModal();
                    }
                    
                    navigate('/');
                  }}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  {t('register.signInLink')}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
